/**
 * Programme partenaires (apporteurs d'affaires) — config centralisée.
 *
 * ACTIF PROPRIÉTAIRE du silo /chauffeur/partenaires : la grille de commission
 * réelle. C'est ce qui rend les pages partenariat uniques et conformes au Core
 * Update (offre concrète, pas du remplissage).
 *
 * Source UNIQUE des taux : tout affichage (pages, tableau, JSON-LD) lit ici.
 * Modifier un taux = un seul endroit.
 *
 * Consommé par app/data/partners/* et les composants /chauffeur/partenaires/*.
 */
import type { Locale } from "@/config/i18n";

export type CommissionTier = {
  /** Borne basse de CA annuel apporté (€, inclus). */
  minRevenue: number;
  /** Borne haute de CA annuel apporté (€, exclus) ; null = pas de plafond. */
  maxRevenue: number | null;
  /** Taux de commission (%). */
  rate: number;
  /** Libellé d'affichage de la tranche, par locale (FR par défaut, EN pour /en). */
  label: { fr: string; en: string };
};

/**
 * Grille de commission apporteur d'affaires (conciergeries, hôtels, agences).
 * Taux appliqués sur le chiffre d'affaires annuel généré par le partenaire.
 */
export const COMMISSION_TIERS: CommissionTier[] = [
  {
    minRevenue: 0,
    maxRevenue: 10000,
    rate: 12,
    label: {
      fr: "0 € – 10 000 € de CA / an",
      en: "€0 – €10,000 of yearly revenue",
    },
  },
  {
    minRevenue: 10000,
    maxRevenue: 40000,
    rate: 17,
    label: {
      fr: "10 000 € – 40 000 € de CA / an",
      en: "€10,000 – €40,000 of yearly revenue",
    },
  },
  {
    minRevenue: 40000,
    maxRevenue: null,
    rate: 22,
    label: {
      fr: "40 000 € de CA / an et plus",
      en: "€40,000+ of yearly revenue",
    },
  },
];

/** Taux minimum et maximum (pour les accroches "jusqu'à X %"). */
export const COMMISSION_RANGE = {
  min: Math.min(...COMMISSION_TIERS.map((t) => t.rate)),
  max: Math.max(...COMMISSION_TIERS.map((t) => t.rate)),
};

/**
 * Renvoie la tranche de commission applicable à un CA annuel apporté (€).
 * Source unique des seuils/taux = COMMISSION_TIERS. Repli sur la 1re tranche si
 * hors bornes (robustesse). Consommé par le calculateur de commission B2B.
 */
export const commissionTierFor = (annualRevenue: number): CommissionTier =>
  COMMISSION_TIERS.find(
    (t) =>
      annualRevenue >= t.minRevenue &&
      (t.maxRevenue === null || annualRevenue < t.maxRevenue),
  ) ?? COMMISSION_TIERS[0];

/**
 * Formate un montant en euros selon la locale, en reproduisant À L'IDENTIQUE
 * la typographie historique du contenu : FR `10 000 €` (espace fine en
 * séparateur de milliers, suffixe « €»), EN `€10,000` (virgule, préfixe «€»),
 * AR `10٬000 يورو` (séparateur de milliers arabe U+066C, suffixe « يورو»).
 * Source unique des montants : COMMISSION_TIERS.
 */
export const formatEuro = (amount: number, locale: Locale): string => {
  const digits = String(amount);
  if (locale === "en") {
    // Séparateur virgule tous les 3 chiffres, préfixe €.
    return `€${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }
  if (locale === "ar") {
    // Séparateur de milliers arabe (U+066C), suffixe « يورو».
    return `${digits.replace(/\B(?=(\d{3})+(?!\d))/g, "٬")} يورو`;
  }
  // FR : séparateur espace, suffixe « €».
  return `${digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`;
};

/** Formate un taux de commission selon la locale (FR/EN `12 %`/`12%`, AR `12٪`). */
export const formatCommissionRate = (rate: number, locale: Locale): string => {
  if (locale === "ar") return `${rate}٪`; // signe pourcent arabe U+066A
  if (locale === "en") return `${rate}%`;
  return `${rate} %`; // FR : espace insécable typographique simplifiée
};

/**
 * Accroche « X–Y % » (fourchette min–max) construite depuis COMMISSION_TIERS,
 * pour les descriptions SEO et titres (« Commission 12 à 22 % » → ne PLUS coder
 * en dur les bornes). FR « 12 à 22 % », EN « 12–22% », AR « 12 إلى 22٪ ».
 */
export const describeCommissionRange = (locale: Locale): string => {
  const { min, max } = COMMISSION_RANGE;
  if (locale === "ar") return `${min} إلى ${max}٪`;
  if (locale === "en") return `${min}–${max}%`;
  return `${min} à ${max} %`;
};

/**
 * Accroche « jusqu'à X % » (taux maximum) depuis COMMISSION_TIERS — pour les
 * titres/badges (« Commission jusqu'à 22 % »). Le libellé « jusqu'à » lui-même
 * reste dans le texte appelant ; ici on ne renvoie que le taux formaté.
 */
export const formatMaxCommissionRate = (locale: Locale): string =>
  formatCommissionRate(COMMISSION_RANGE.max, locale);

/**
 * Construit la PHRASE de grille de commission (taux + seuils) depuis
 * COMMISSION_TIERS, par locale. Évite de coder en dur « 12 % … 17 % … 22 % »
 * dans les FAQ de app/data/partners/segments.ts : si un taux ou un seuil change
 * dans COMMISSION_TIERS, la prose suit automatiquement (plus de désync).
 *
 * La grille attendue comporte 3 tranches (plafonnée, intermédiaire, ouverte) :
 *   - tranche 0 : « <r0> jusqu'à <maxRevenue0> »
 *   - tranche 1 : « <r1> entre <minRevenue1> et <maxRevenue1> »
 *   - tranche 2 : « <r2> au-delà de <minRevenue2> »
 * Reproduit la formulation historique mot pour mot (hors valeurs interpolées),
 * dans les trois langues, pour préserver le SEO et le ton AR (MSA).
 */
export const describeCommissionGrid = (locale: Locale): string => {
  const [t0, t1, t2] = COMMISSION_TIERS;
  const r = (rate: number) => formatCommissionRate(rate, locale);
  const e = (amount: number) => formatEuro(amount, locale);

  if (locale === "en") {
    return (
      `${r(t0.rate)} up to ${e(t0.maxRevenue ?? 0)} of yearly referred revenue, ` +
      `${r(t1.rate)} between ${e(t1.minRevenue)} and ${e(t1.maxRevenue ?? 0)}, ` +
      `and ${r(t2.rate)} above ${e(t2.minRevenue)}`
    );
  }
  if (locale === "ar") {
    // « بين X و Y يورو » : l'unité « يورو» ne porte que sur la borne haute
    // (formulation historique), d'où le séparateur de milliers nu pour X.
    const arThousands = (amount: number) =>
      String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, "٬");
    return (
      `${r(t0.rate)} حتى ${e(t0.maxRevenue ?? 0)} من الإيرادات السنوية المُحالة، ` +
      `و${r(t1.rate)} بين ${arThousands(t1.minRevenue)} و${e(t1.maxRevenue ?? 0)}، ` +
      `و${r(t2.rate)} فوق ${e(t2.minRevenue)}`
    );
  }
  // FR (par défaut)
  return (
    `${r(t0.rate)} jusqu'à ${e(t0.maxRevenue ?? 0)} de CA annuel apporté, ` +
    `${r(t1.rate)} entre ${e(t1.minRevenue)} et ${e(t1.maxRevenue ?? 0)}, ` +
    `et ${r(t2.rate)} au-delà de ${e(t2.minRevenue)}`
  );
};

/** Engagements clés du programme (réutilisés en page + JSON-LD). */
export const PARTNER_PROGRAM = {
  /** Villes / zones couvertes par le service chauffeur. */
  citiesServed: [
    "Cannes",
    "Nice",
    "Monaco",
    "Antibes",
    "Saint-Tropez",
    "Saint-Jean-Cap-Ferrat",
  ],
  /** Promesses opérationnelles vérifiables. */
  commitments: [
    "Commission versée sur 100 % du CA réellement encaissé.",
    "Interlocuteur dédié et reporting clair des courses apportées.",
    "Chauffeurs en tenue, véhicules premium, discrétion absolue.",
    "Disponibilité 24/7, suivi de vol et coordination conciergerie.",
  ],
};
